<script setup lang="ts">
// Modal de notificações da tela de jogo (Smartico inbox). Casca slide-up
// (mesmo padrão do modal de depósito), layout conforme Figma (node 16209:20966).

const { isMobile } = useLayout();
const { isOpen, close } = useGameNotificationsModal();

const { allInboxMessages, isReadyMessages } = storeToRefs(
  useSmarticoMessages(),
);

const { loadMessages, markAllInboxMessagesAsRead } = useSmarticoMessages();

type TabId = 'todos' | 'favoritos';

const tabs: { id: TabId; label: string }[] = [
  { id: 'todos', label: 'Todos' },
  { id: 'favoritos', label: 'Favoritos' },
];

const activeTab = ref<TabId>('todos');

const isLoading = ref(false);

// Não lidas primeiro; filtra por aba.
const messages = computed(() => {
  const list = [...(allInboxMessages.value || [])].sort((a, b) =>
    a.read === b.read ? 0 : a.read ? 1 : -1,
  );

  if (activeTab.value === 'favoritos') {
    return list.filter((m) => m.favorite);
  }

  return list;
});

const hasUnread = computed(() =>
  (allInboxMessages.value || []).some((m) => !m.read),
);

async function refresh() {
  isLoading.value = true;
  await loadMessages();
  isLoading.value = false;
}

async function markAllAsRead() {
  await markAllInboxMessagesAsRead();
  await refresh();
}

// Carrega ao abrir; reseta a aba.
watch(isOpen, (open) => {
  if (!open) return;
  activeTab.value = 'todos';
  refresh();
});

// Trava o scroll do body + guard anti rubber-band do iOS (compartilhado).
const { scrollerRef } = useSlideUpSheet(isOpen);
</script>

<template>
  <Teleport to="body">
    <Transition
      appear
      :duration="300"
      name="gnm"
    >
      <div
        v-if="isOpen"
        class="gnm-overlay"
        :class="{ 'is-desktop': !isMobile }"
        @click.self="close"
      >
        <div
          class="gnm-sheet"
          :class="{ 'is-desktop': !isMobile }"
        >
          <!-- Topbar -->
          <header class="gnm-topbar">
            <span class="gnm-title">Notificações</span>
            <div class="gnm-topbar-actions">
              <button
                v-if="hasUnread"
                class="gnm-readall"
                type="button"
                @click="markAllAsRead"
              >
                Marcar todas como lidas
              </button>
              <button
                aria-label="Fechar"
                class="gnm-close"
                type="button"
                @click="close"
              >
                <svg
                  fill="none"
                  height="10"
                  viewBox="0 0 14 14"
                  width="10"
                >
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </div>
          </header>

          <!-- Tabs -->
          <div class="gnm-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="gnm-tab"
              :class="{ 'is-active': activeTab === tab.id }"
              type="button"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <!-- Lista -->
          <div
            ref="scrollerRef"
            class="gnm-list"
          >
            <template v-if="isLoading || !isReadyMessages">
              <div
                v-for="n in 4"
                :key="`sk-${n}`"
                class="gnm-skeleton"
              />
            </template>

            <template v-else-if="messages.length">
              <CasinoGameNotificationsModalItem
                v-for="m in messages"
                :key="m.message_guid"
                :notification="m"
              />
            </template>

            <p
              v-else
              class="gnm-empty"
            >
              {{ $t('notifications.no_results') }}
            </p>
          </div>

          <div class="gnm-safe-bottom" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
$primary: #1414dd;

// Transição única (v-if só no overlay): só o .gnm-sheet desliza, via
// `animation` (keyframes). NÃO usar `transition`: o reset global em main.scss
// tem `*:not(.transition){ transition: none !important }`, que mata transition
// no app. O backdrop (overlay) NÃO faz fade de propósito — anima-lo junto com
// o `backdrop-filter: blur` full-screen recompõe o blur a cada frame (caro em
// mobile fraco). O Vue não auto-detecta duração no overlay (sem animation),
// então a prop :duration="300" no <Transition> diz o tempo explícito.

.gnm-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgba(2, 7, 22, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  &.is-desktop {
    justify-content: center;
    align-items: center;
  }
}

.gnm-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-height: 88dvh;
  padding: 16px 16px 32px;
  background: #000717;
  border-radius: 20px 20px 0 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  will-change: transform, opacity;

  &.is-desktop {
    width: auto;
    max-width: 433px;
    min-width: 400px;
    border-radius: 16px;
    box-shadow:
      0 14px 28px 0 rgba(0, 0, 0, 0.3),
      inset 0 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}

// Slide do sheet via keyframe global slide-up-spring (translateY + opacity),
// atrelado ao enter/leave do overlay. Declarado depois de .gnm-sheet por causa
// do no-descending-specificity do stylelint. Animation (não transition) pra
// escapar do reset global `transition: none !important`.
.gnm-enter-active .gnm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

// `forwards` é essencial: segura o último frame (fora da tela) até o Vue
// remover. Sem ele, ao fim da animação o sheet volta ao estado base (aberto)
// por 1-2 frames antes da remoção — daí o flash do modal aberto.
.gnm-leave-active .gnm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
}

.gnm-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .gnm-title {
    color: #fff;
    font-weight: 700;
    font-size: 15px;
    line-height: 16px;
  }

  .gnm-topbar-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .gnm-readall {
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .gnm-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.05);
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
    }
  }
}

.gnm-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .gnm-tab {
    flex-shrink: 0;
    height: 32px;
    padding: 0 12px;
    border-radius: 12px;
    background: #272f3f;
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    cursor: pointer;
    transition: background 0.15s ease;

    &.is-active {
      background: $primary;
    }
  }
}

.gnm-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-height: 50dvh;
  min-height: 120px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.gnm-skeleton {
  flex-shrink: 0;
  width: 100%;
  height: 82px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  animation: gnm-pulse 1.2s ease-in-out infinite;
}

@keyframes gnm-pulse {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

.gnm-empty {
  padding: 32px 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.gnm-safe-bottom {
  height: env(safe-area-inset-bottom, 0px);
}
</style>
